import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";

type Option = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

type Props = {
  value: string | undefined;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  size?: "sm" | "md" | "md-x" | "lg" | "xl" | "full";
  className?: string;
  contentClassName?: string;
  showIndicator?: boolean;
  disabled?: boolean;
};

const SIZE_STYLES = {
  sm: {
    trigger: "h-9 w-[70px] text-sm",
    content: "w-[70px] text-xs",
  },
  md: {
    trigger: "h-9 w-[90px] text-sm",
    content: "w-[90px] text-sm",
  },
  lg: {
    trigger: "h-9 w-[160px] text-sm",
    content: "w-[160px] text-sm",
  },
  xl: {
    trigger: "h-9 w-[190px] text-sm",
    content: "w-[190px] text-sm",
  },
  "md-x": {
    trigger: "h-9 w-[140px] text-sm",
    content: "w-[140px] text-sm",
  },
  full: {
    trigger: "h-auto w-full py-2.5 text-sm",
    content: "w-[var(--radix-select-trigger-width)] text-sm",
  },
};

export default function FilterSelect({
  value,
  onChange,
  options,
  placeholder = "Select",
  size = "sm", // default is sm
  className = "",
  contentClassName = "",
  disabled = false,
}: Props) {
  const styles = SIZE_STYLES[size];

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={`
          ${styles.trigger}
          rounded-md
          border border-slate-200
          bg-white
          text-[var(--custom-gray-700)]
          font-normal
          shadow-sm
          hover:bg-gray-50
          focus:ring-2 focus:ring-[#f9f9fa]
          cursor-pointer
          ${disabled ? "disabled" : ""}
          ${className}
        `}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>

      <SelectContent
        className={`
          ${styles.content}
          rounded-md
          border border-[#D8DCE1]
          bg-white
          shadow-lg
          py-2
          font-normal
          text-[var(--custom-gray-800)]
          ${contentClassName}
        `}
        position="popper"
        sideOffset={6}
      >
        {options.map((opt) => {
          const hasDescription = !!opt.description;

          return (
            <SelectItem
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              className={`
                cursor-pointer  focus:bg-gray-100
                ${hasDescription
                  ? "pl-2 pr-4 py-2 flex items-start"
                  : "px-2 py-2"
                }
                ${opt.disabled ? "opacity-40 cursor-not-allowed grayscale-[0.5]" : ""}
      `}
            >
              <div
                className={`flex flex-col text-left ${hasDescription ? "gap-1" : ""}`}
              >
                <span
                  className={`text-sm ${hasDescription ? "font-bold" : "font-normal"}`}
                >
                  {opt.label}
                </span>

                {hasDescription && (
                  <span className="text-xs text-slate-500">
                    {opt.description}
                  </span>
                )}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
